// Product Search Lock — frontend integration.
//
// The backend puts a 45s auto-expiring lease on a product when a user searches /
// opens it, so two people can't edit the same item at once. This module is the
// small client the rest of the app talks to. Three responsibilities:
//   1. Turn the backend's `409 { status: "locked" }` into a user-facing message
//      instead of a wrong "not found" / silent clear.
//   2. Keep the lease alive with a heartbeat (~15s) while a user is viewing an item.
//   3. Release the lease when the user moves on or leaves (best-effort; the 45s
//      TTL is the real safety net).
//
// Backend contract lives in Util/searchLock.js (SEARCH_LOCK_TTL_MS = 45000).
import Instance from "./Instance";
import { toast } from "react-toastify";

// Must stay well under the backend's 45s lease so an actively-viewing user is
// never kicked mid-work. Do not raise above ~30s.
export const SEARCH_HEARTBEAT_MS = 15000;

// Fallback for the (already-Kurdish) "in use by another user" message, used only
// if the backend ever omits `msg`.
const LOCKED_FALLBACK_MSG =
  "ئەم کاڵایە ئێستا لەلایەن بەکارهێنەرێکی ترەوە بەکاردەهێنرێت";

// Single active lease per app instance (the app is already single-tab). Searching
// a new product releases the previous one automatically.
let heartbeatTimer = null;
let currentCode = null;
let onLostLock = null;

// True when an axios error is the backend's "locked by another user" 409.
export function isSearchLocked(error) {
  const r = error && error.response;
  return !!(r && r.status === 409 && r.data && r.data.status === "locked");
}

// The Kurdish "in use by another user" message from the 409 (with a fallback).
export function lockedMessage(error) {
  const r = error && error.response;
  return (r && r.data && r.data.msg) || LOCKED_FALLBACK_MSG;
}

// Best-effort release via the normal axios instance. Never throws.
export function releaseSearch(code) {
  if (!code) return;
  try {
    Instance.get(
      `/api/v1/releaseSearch?code=${encodeURIComponent(code)}`
    ).catch(() => {});
  } catch (e) {
    /* ignore — the 45s TTL is the real guarantee */
  }
}

// Stop the heartbeat and (by default) release the held lock. Pass
// { release: false } when the lock is already gone (e.g. we just lost it).
export function stopSearchHeartbeat(opts) {
  const release = !opts || opts.release !== false;
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  const code = currentCode;
  currentCode = null;
  onLostLock = null;
  if (code && release) releaseSearch(code);
}

// Start (or restart) the heartbeat for `code`, releasing any previously-held code
// first. `onLost(msg)` fires if the heartbeat ever comes back 409 (rare — we lost
// the lease to someone else).
export function startSearchHeartbeat(code, onLost) {
  stopSearchHeartbeat();
  if (!code) return;
  currentCode = code;
  onLostLock = typeof onLost === "function" ? onLost : null;
  heartbeatTimer = setInterval(async () => {
    try {
      await Instance.get(
        `/api/v1/searchHeartbeat?code=${encodeURIComponent(code)}`
      );
    } catch (error) {
      if (isSearchLocked(error)) {
        const cb = onLostLock;
        const msg = lockedMessage(error);
        // We no longer hold it — stop without a pointless release call.
        stopSearchHeartbeat({ release: false });
        if (cb) cb(msg);
      }
      // Network / 404: ignore. The TTL covers us and the next tick may recover.
    }
  }, SEARCH_HEARTBEAT_MS);
}

// GET a lock-aware search URL. Behaviour:
//   • locked 409 → shows the Kurdish message and returns null (caller stops).
//   • success with a product → starts the heartbeat so this user holds the lease.
//   • success with no product (not found) → releases any prior hold, returns res.
//   • any other error → propagates to the caller's existing catch.
// Pass { heartbeat: false } for read-only / report / multi-select screens that
// should degrade gracefully on a 409 but never hold the lease themselves.
export async function searchWithLock(url, config, onLost, options) {
  const wantHeartbeat = !options || options.heartbeat !== false;
  try {
    const res = await Instance.get(url, config);
    if (wantHeartbeat) {
      const product = res && res.data && res.data.product;
      if (product && product.code) startSearchHeartbeat(product.code, onLost);
      else stopSearchHeartbeat(); // moved off the previous item → release it
    }
    return res;
  } catch (error) {
    if (isSearchLocked(error)) {
      toast.error(lockedMessage(error));
      return null;
    }
    throw error;
  }
}

// 409-graceful GET that never holds the lease (report / multi-select screens).
export function searchHandleLock(url, config) {
  return searchWithLock(url, config, null, { heartbeat: false });
}

// Unload release. Uses fetch + keepalive (NOT navigator.sendBeacon) so the call
// survives navigation AND can still send the Authorization header — the backend's
// `protect` middleware rejects header-less requests, which a beacon would be.
export function releaseSearchOnUnload() {
  if (!currentCode) return;
  const code = currentCode;
  try {
    fetch(
      `${Instance.defaults.baseURL}/api/v1/releaseSearch?code=${encodeURIComponent(
        code
      )}`,
      {
        method: "GET",
        headers: { Authorization: "Bearer " + localStorage.token },
        keepalive: true
      }
    ).catch(() => {});
  } catch (e) {
    /* ignore — the 45s TTL is the real guarantee */
  }
}
